import { LS_MY_EVENTS, MY_EVENTS_CAP, POLL_TAG } from './constants.js'

// NIP-88 polls. New polls are kind 1068 with "option" tags so Primal and
// other NIP-88 clients render them as votable polls; votes are kind 1018
// with a "response" tag holding the option id. Legacy Verdant polls are
// kind 1 with "poll_option" tags and kind 1 vote replies; both formats
// are read side by side.
export const POLL_KIND = 1068
export const POLL_RESPONSE_KIND = 1018

// Read the first matching tag's value at index 1.
function tagValue(event, name) {
  const t = event.tags?.find((tag) => tag[0] === name)
  return t ? t[1] : null
}

// Read all tag rows for a given name.
function tagRows(event, name) {
  return (event.tags || []).filter((tag) => tag[0] === name)
}

function tagValues(event, name) {
  return tagRows(event, name).map((tag) => tag[1]).filter(Boolean)
}

// Try to extract a bill ID like "HR6387" from t-tags or content.
function extractBillId(event) {
  const tValues = tagValues(event, 't')
  const candidate = tValues.find((v) =>
    /^(HR|S|HRES|SRES|HJRES|SJRES|HCONRES|SCONRES)\d+$/i.test(v)
  )
  if (candidate) return candidate.toUpperCase()
  const m = event.content?.match(/\b(HR|S|HRES|SRES|HJRES|SJRES|HCONRES|SCONRES)\s*\d+\b/i)
  return m ? m[0].replace(/\s+/g, '').toUpperCase() : null
}

// Strip the marketing prefix "🗳️ VOTE: " or "🗳️ RACE: " etc.
// Then take the first non-empty line as the title.
function extractTitle(event) {
  const raw = (event.content || '').trim()
  if (!raw) return ''
  const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean)
  const first = lines[0] || ''
  return first
    .replace(/^[^\w]*VOTE:\s*/i, '')
    .replace(/^[^\w]*RACE:\s*/i, '')
    .replace(/^["']|["']$/g, '')
    .trim()
}

const LEVEL_LABEL = {
  federal: 'Federal',
  state: 'State',
  city: 'City',
  user: 'User-proposed',
}

function jurisdictionLabel(jurisdiction, level) {
  if (!jurisdiction) return LEVEL_LABEL[level] || ''
  // US-WA-Seattle -> Seattle, WA
  // US-WA -> Washington (state name lookup deferred — show code)
  // US -> Federal
  const parts = jurisdiction.split('-')
  if (parts.length === 1 && parts[0] === 'US') return 'Federal'
  if (parts.length === 2) return parts[1]
  if (parts.length >= 3) return `${parts[2]}, ${parts[1]}`
  return jurisdiction
}

// Normalize a choice/option label to canonical Yes/No/Unsure. Older bot
// posts and historic vote events used Aye/Nay/Abstain; we map those
// forward so the rest of the app can assume one vocabulary.
export function normalizeChoice(s) {
  const c = (s || '').trim().toLowerCase()
  if (!c) return ''
  if (c === 'yes' || c === 'aye') return 'Yes'
  if (c === 'no'  || c === 'nay') return 'No'
  if (c.startsWith('uns') || c.startsWith('abst')) return 'Unsure'
  return s
}

// Option rows in display order: legacy "poll_option" (sorted by numeric
// id) or NIP-88 "option" (author order).
function pollOptionRows(event) {
  const legacy = tagRows(event, 'poll_option')
  if (legacy.length) return legacy.sort((a, b) => Number(a[1] || 0) - Number(b[1] || 0))
  return tagRows(event, 'option')
}

function parsePollOptions(event) {
  const rows = pollOptionRows(event)
  if (!rows.length) return { options: ['Yes', 'No', 'Unsure'], optionIds: {} }
  const options = []
  const optionIds = {}
  for (const row of rows) {
    const raw = row[2] || row[1] || ''
    // Bot labels vary: "✅ Yes", "✅ Aye (Yes)", "❌ No", "❌ Nay (No)",
    // "⬜ Abstain", "⬜ Unsure". Match any of the canonical forms.
    const match = raw.match(/Yes|Aye|No|Nay|Unsure|Abstain/i)
    const label = match ? normalizeChoice(match[0]) : raw.trim()
    options.push(label)
    if (row[1] != null) optionIds[label] = row[1]
  }
  return { options, optionIds }
}

// Candidate name -> NIP-88 option id. Bot labels read "Name (Party)" so
// Primal shows the party; strip that suffix to match the candidate tag.
function candidateOptionIds(event) {
  const out = {}
  for (const row of tagRows(event, 'option')) {
    if (row[1] == null || !row[2]) continue
    out[row[2].trim().replace(/\s+\([^)]*\)$/, '')] = row[1]
  }
  return out
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function parseEndsAt(event) {
  const v = tagValue(event, 'endsAt')
  if (!v) return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  return new Date(n < 1e12 ? n * 1000 : n)
}

export function isPollEvent(event) {
  if (!event || (event.kind !== 1 && event.kind !== POLL_KIND)) return false
  const tags = event.tags || []
  // Exclude vote events. Per SPEC §6 votes carry the same #ThePeopleVote
  // tag for discoverability, but they also have a "vote" tag pointing at
  // the chosen option — that's the discriminator between a poll and a
  // vote-on-a-poll.
  if (tags.some((t) => t[0] === 'vote')) return false
  return tags.some((t) => t[0] === 't' && t[1] === POLL_TAG)
}

// True if the event is a vote reply (tags a poll event with a "vote" tag).
// Also true for NIP-88 kind 1018 responses (any client, e.g. Primal).
export function isVoteEvent(event) {
  if (!event) return false
  const tags = event.tags || []
  const hasReplyE = tags.some((t) => t[0] === 'e')
  if (event.kind === POLL_RESPONSE_KIND) {
    return hasReplyE && tags.some((t) => t[0] === 'response')
  }
  if (event.kind !== 1) return false
  const hasVote = tags.some((t) => t[0] === 'vote')
  return hasVote && hasReplyE
}

// Find the poll event id this vote replies to. Prefer the explicit "reply"
// marker; fall back to the first e-tag.
export function votePollId(event) {
  const eTags = tagRows(event, 'e')
  const reply = eTags.find((t) => t[3] === 'reply')
  return (reply || eTags[0])?.[1] || null
}

// `poll` (a parsed item) is needed to read kind 1018 responses from other
// clients, which carry only an option id. Verdant's own 1018s also carry a
// "vote" label, so they resolve without it.
export function voteChoice(event, poll) {
  const label = tagValue(event, 'vote')
  if (label) return label
  if (event.kind === POLL_RESPONSE_KIND) {
    const id = tagValue(event, 'response')
    const ids = poll?.optionIds || {}
    return Object.keys(ids).find((l) => ids[l] === id) || null
  }
  return (event.content || '').trim() || null
}

export function voteJurisdiction(event) {
  return tagValue(event, 'jurisdiction')
}

export function parseEvent(event) {
  if (!isPollEvent(event)) return null

  const isCandidate =
    tagValue(event, 'type') === 'candidate' || tagValue(event, 'race')

  const level = tagValue(event, 'level') || (isCandidate ? 'city' : 'federal')
  const jurisdiction = tagValue(event, 'jurisdiction') || 'US'
  const endsAt = parseEndsAt(event)
  const postedAt = new Date((event.created_at || 0) * 1000)
  // Newer posts open with a question for other clients and carry the
  // headline in a "title" tag; older posts put it on the first line.
  const title = tagValue(event, 'title') || extractTitle(event)
  const plainSummary = tagValue(event, 'plain_summary') || null
  const sourceUrl = tagValue(event, 'r')
  const categories = tagValues(event, 'category')

  if (isCandidate) {
    const candidates = tagRows(event, 'candidate').map((row) => ({
      name: row[1] || '',
      party: row[2] || '',
    }))
    const office = tagValue(event, 'office') || ''
    const electionDateRaw = tagValue(event, 'election_date')
    const electionDate = electionDateRaw ? new Date(electionDateRaw) : null

    return {
      id: event.id,
      kind: 'candidate',
      raceId: tagValue(event, 'race') || event.id.slice(0, 10),
      level,
      jurisdiction,
      levelLabel: jurisdictionLabel(jurisdiction, level),
      title,
      candidates,
      office,
      electionDate,
      endsAt,
      postedAt,
      pubkey: event.pubkey,
      categories,
      nostrKind: event.kind,
      optionIds: candidateOptionIds(event),
      resident: null,
    }
  }

  const { options, optionIds } = parsePollOptions(event)

  return {
    id: event.id,
    kind: 'bill',
    billId: extractBillId(event),
    level,
    jurisdiction,
    levelLabel: jurisdictionLabel(jurisdiction, level),
    title,
    plainSummary,
    sourceUrl,
    categories,
    options,
    optionIds,
    nostrKind: event.kind,
    endsAt,
    postedAt,
    pubkey: event.pubkey,
    // UX57: which Verdant bot authored this poll. 'pulse' = daily
    // sentiment question (no stage/last-action to show); other values
    // are legislative/candidate sources. Downstream cards gate the
    // legislative TL;DR strip on this.
    botName: tagValue(event, 'bot') || '',
    // Legislative stage (introduced / committee / floor / passed /
    // signed / vetoed / dead). Phase 2 backend will keep this current.
    billStage: tagValue(event, 'bill_stage') || 'introduced',
    // UX67: snapshot of the latest legislative action, straight from
    // the bot event tags. Used by the timeframe filter + smart sort
    // when billActions (server overlay) is empty.
    lastAction: tagValue(event, 'last_action') || '',
    lastActionDate: tagValue(event, 'last_action_date') || '',
    // Phase 2: bill_actions array populated from Congress.gov API via
    // snapshot_job → public/snapshots.json → Home.jsx overlay.
    billActions: [],
    resident: null,
  }
}

// Build an empty tally object for given options.
export function emptyTally(options) {
  const out = {}
  for (const opt of options) {
    out[opt] = { resident: 0, nonResident: 0 }
  }
  return out
}

// Decide if the voter is a resident relative to the user's jurisdiction.
// Phase 1 rule: voter has a jurisdiction tag AND it shares a prefix with
// the user's jurisdiction at the level of the poll.
export function isResidentVote(voteJurisdictionTag, userJurisdiction, pollJurisdiction) {
  if (!voteJurisdictionTag) return false
  if (!userJurisdiction) return false
  // For a federal poll any US voter is resident.
  if (pollJurisdiction === 'US') {
    return voteJurisdictionTag.startsWith('US') && userJurisdiction.startsWith('US')
  }
  // Otherwise the voter's jurisdiction must contain the poll jurisdiction
  // (e.g. voter US-WA-Seattle for a US-WA poll).
  return voteJurisdictionTag.startsWith(pollJurisdiction)
}

// ---------- "my events" cache ----------
// Persists signed events the user has authored so they survive reloads
// even when relays drop them silently.

export function loadMyEvents() {
  try {
    const raw = localStorage.getItem(LS_MY_EVENTS)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export function rememberMyEvent(signed) {
  if (!signed?.id) return
  const events = loadMyEvents()
  if (events.find((e) => e.id === signed.id)) return
  events.unshift(signed)
  const trimmed = events.slice(0, MY_EVENTS_CAP)
  try { localStorage.setItem(LS_MY_EVENTS, JSON.stringify(trimmed)) } catch {
    /* quota exceeded — drop oldest until it fits */
    try { localStorage.setItem(LS_MY_EVENTS, JSON.stringify(trimmed.slice(0, 50))) } catch { /* noop */ }
  }
}

// Build a vote event template (unsigned). For NIP-88 polls (pass the
// parsed `poll`) this is a kind 1018 response that Primal and other
// clients count; the extra "vote" and "jurisdiction" tags keep Verdant's
// residency tallies working. Legacy kind 1 polls get a kind 1 reply.
export function buildVoteEvent({ pollEventId, pollAuthor, choice, userJurisdiction, poll }) {
  if (poll?.nostrKind === POLL_KIND) {
    const ids = poll.optionIds || {}
    const key = Object.keys(ids).find((l) => l.toLowerCase() === String(choice).toLowerCase())
    if (key) {
      const tags = [
        ['e', pollEventId],
        ['p', pollAuthor],
        ['response', ids[key]],
        ['vote', choice],
      ]
      if (userJurisdiction) tags.push(['jurisdiction', userJurisdiction])
      return {
        kind: POLL_RESPONSE_KIND,
        created_at: Math.floor(Date.now() / 1000),
        content: '',
        tags,
      }
    }
  }
  const tags = [
    ['e', pollEventId, '', 'reply'],
    ['p', pollAuthor],
    ['vote', choice],
    ['t', POLL_TAG],
  ]
  if (userJurisdiction) tags.push(['jurisdiction', userJurisdiction])
  return {
    kind: 1,
    created_at: Math.floor(Date.now() / 1000),
    content: choice,
    tags,
  }
}

// NIP-18 quote repost. Kind:1 event referencing an existing poll via a
// "q" tag — most Nostr clients render this as a quoted post. We also
// include an "e" mention so older clients still surface the link.
// Optional `comment` is the user's own note; when set it's prepended
// to the content above the title and URL.
export function buildQuoteEvent({ pollEventId, pollAuthor, title, shareUrl, comment, content: overrideContent }) {
  // If the caller passed `content` explicitly (the preview-and-confirm
  // path), use that verbatim — the user has already seen and edited
  // the final text they want posted. Otherwise fall back to the
  // comment + title + URL concatenation.
  const safeTitle = (title || 'a poll on Verdant').trim()
  const safeComment = (comment || '').trim()
  const fallbackContent = safeComment
    ? `${safeComment}\n\n${safeTitle}\n\n${shareUrl}`
    : `${safeTitle}\n\n${shareUrl}`
  const content = typeof overrideContent === 'string' && overrideContent.trim()
    ? overrideContent.trim()
    : fallbackContent
  return {
    kind: 1,
    created_at: Math.floor(Date.now() / 1000),
    content,
    tags: [
      ['q', pollEventId, '', pollAuthor],
      ['e', pollEventId, '', 'mention'],
      ['p', pollAuthor],
      ['t', POLL_TAG],
    ],
  }
}

// UX38: assemble the default preview text the user sees + edits in the
// "Review your Nostr post" step. Exported so ShareDialog can show it
// without having to know the quote-event structure.
export function defaultNostrPostText({ title, shareUrl, comment }) {
  const safeTitle = (title || 'a poll on Verdant').trim()
  const safeComment = (comment || '').trim()
  return safeComment
    ? `${safeComment}\n\n${safeTitle}\n\n${shareUrl}`
    : `${safeTitle}\n\n${shareUrl}`
}
